<script setup>
  import { ref, computed, watch } from "vue";
  import { Button } from "@/components/ui/button";
  import { Textarea } from "@/components/ui/textarea";
  import { Avatar, AvatarFallback } from "@/components/ui/avatar";
  import { Send } from "lucide-vue-next";
  import { useReviewCommentsStore } from "@stores/survey/reviewComments";
  import { segmentMentions } from "./mentionSegments";

  const props = defineProps({
    placeholder: { type: String, default: "Add a comment…" },
    initialBody: { type: String, default: "" },
    mode: { type: String, default: "create" },
  });
  const emit = defineEmits(["submit", "cancel"]);

  const store = useReviewCommentsStore();
  const body = ref(props.initialBody);
  const mentionQuery = ref(null);
  const activeIndex = ref(0);

  const TRAILING_MENTION_PATTERN = /(^|\s)@([\p{L}\w]*)$/u;
  watch(body, () => {
    const match = body.value.match(TRAILING_MENTION_PATTERN);
    mentionQuery.value = match ? match[2].toLowerCase() : null;
    activeIndex.value = 0;
  });

  const suggestions = computed(() => {
    if (mentionQuery.value === null) return [];
    return store.mentionableUsers.filter((user) => user.full_name.toLowerCase().includes(mentionQuery.value)).slice(0, 6);
  });

  const segments = computed(() => segmentMentions(body.value, store.mentionableUsers));

  const pick = (user) => {
    body.value = body.value.replace(TRAILING_MENTION_PATTERN, (matched, prefix) => `${prefix}@${user.full_name} `);
    mentionQuery.value = null;
  };

  const mentionIds = () =>
    store.mentionableUsers.filter((user) => body.value.includes(`@${user.full_name}`)).map((user) => user.id);

  const submit = () => {
    const text = body.value.trim();
    if (!text) return;
    emit("submit", text, mentionIds());
    body.value = "";
    mentionQuery.value = null;
  };

  const moveActive = (event, direction) => {
    if (!suggestions.value.length) return;
    event.preventDefault();
    const count = suggestions.value.length;
    activeIndex.value = (activeIndex.value + direction + count) % count;
  };
  const onEnter = () => {
    if (suggestions.value.length) pick(suggestions.value[activeIndex.value]);
    else submit();
  };
</script>

<template>
  <div class="relative flex gap-2 items-end">
    <div
      v-if="suggestions.length"
      class="absolute bottom-full left-0 mb-1 w-60 rounded-lg border bg-white shadow-lg overflow-hidden z-20">
      <Button
        v-for="(user, index) in suggestions"
        :key="user.id"
        variant="ghost"
        class="w-full justify-start gap-2 h-auto rounded-none px-2.5 py-1.5 text-sm font-normal"
        :class="{ 'bg-muted': index === activeIndex }"
        @mouseenter="activeIndex = index"
        @click="pick(user)">
        <Avatar class="h-5 w-5">
          <AvatarFallback class="text-3xs bg-secondary text-foreground-secondary">{{ user.initials }}</AvatarFallback>
        </Avatar>
        <span class="truncate">{{ user.full_name }}</span>
        <span class="ml-auto text-3xs text-muted-foreground uppercase">{{
          user.kind === "csm" ? "CSM" : "Team"
        }}</span>
      </Button>
    </div>

    <div class="relative flex-1">
      <div
        aria-hidden="true"
        class="absolute inset-0 overflow-hidden whitespace-pre-wrap break-words rounded-md border border-transparent px-3 py-2 text-sm leading-5 pointer-events-none">
        <template
          v-for="(segment, index) in segments"
          :key="index"
          ><span
            v-if="segment.mention"
            class="text-status-info-strong"
            >{{ segment.text }}</span
          ><span v-else>{{ segment.text }}</span></template
        ><span>&#8203;</span>
      </div>
      <Textarea
        v-model="body"
        :placeholder="placeholder"
        rows="1"
        class="relative min-h-0 w-full resize-none text-sm leading-5 text-transparent caret-foreground"
        @keydown.down="(event) => moveActive(event, 1)"
        @keydown.up="(event) => moveActive(event, -1)"
        @keydown.enter.exact.prevent="onEnter"
        @keydown.esc="mentionQuery = null" />
    </div>

    <div
      v-if="mode === 'edit'"
      class="flex shrink-0 items-center gap-1">
      <Button
        variant="ghost"
        size="sm"
        class="h-8 px-2 text-xs font-normal text-muted-foreground"
        @click="emit('cancel')">
        Cancel
      </Button>
      <Button
        size="sm"
        class="h-8 px-2.5 text-xs"
        :disabled="!body.trim()"
        @click="submit">
        Save
      </Button>
    </div>
    <Button
      v-else
      size="icon"
      class="shrink-0"
      :disabled="!body.trim()"
      @click="submit">
      <Send class="h-4 w-4" />
    </Button>
  </div>
</template>
